/**
 * Agent Eval Redux Slice
 *
 * State management for real-time AI agent evaluation during calls.
 * Tracks streaming status, transcript segments, and AI proposals.
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { TranscriptSegment, AgentProposal, FlowContext, FlowTask, FlowCompletedEvent, TaskProgressEvent, EncounterFlowsCompleteEvent, ProposalDecision, ExecutionStatus, SlotFill, AdhocFlowStartedEvent, AdhocFlowCompletedEvent, AdhocOrigin } from '../services/agent-eval.service'

export type StreamingStatus = 'idle' | 'connecting' | 'streaming' | 'paused' | 'stopped' | 'error'

export interface AgentEvalState {
  // Streaming state
  status: StreamingStatus
  sessionId: string | null
  duration: number

  // Patient context
  patientId: string | null
  patientName: string | null

  // Transcript data
  transcriptSegments: TranscriptSegment[]
  proposals: AgentProposal[]

  // Active flow context (from the gateway's flow executor)
  flowContext: FlowContext | null
  dismissedFlows: Array<{ flow_name: string; reason: string }> // Track dismissed flows with reasons

  // Flow orchestration (task-driven auto-advance)
  flowTaskQueue: FlowTask[]
  currentFlowTask: FlowTask | null
  completedFlowTasks: FlowTask[]
  flowStatus: 'idle' | 'running' | 'paused' | 'complete'
  advanceMode: 'auto' | 'hybrid' | 'manual'

  // Ad-hoc flow tracking
  currentAdhocFlow: AdhocFlowStartedEvent | null
  adhocFlowTasks: AdhocFlowCompletedEvent[]

  // Error state
  error: string | null

  // UI state
  panelExpanded: boolean
  transcriptVisible: boolean
  proposalsVisible: boolean
}

const initialState: AgentEvalState = {
  status: 'idle',
  sessionId: null,
  duration: 0,
  patientId: null,
  patientName: null,
  transcriptSegments: [],
  proposals: [],
  flowContext: null,
  dismissedFlows: [],
  flowTaskQueue: [],
  currentFlowTask: null,
  completedFlowTasks: [],
  flowStatus: 'idle',
  advanceMode: 'hybrid',
  currentAdhocFlow: null,
  adhocFlowTasks: [],
  error: null,
  panelExpanded: true,
  transcriptVisible: true,
  proposalsVisible: true
}

export interface StartStreamingPayload {
  patientId: string
  patientName?: string
  sessionId: string
}

export const agentEvalSlice = createSlice({
  name: 'agentEval',
  initialState,
  reducers: {
    // Session management
    startStreaming: (state, action: PayloadAction<StartStreamingPayload>) => {
      state.status = 'streaming'
      state.patientId = action.payload.patientId
      state.patientName = action.payload.patientName || null
      state.sessionId = action.payload.sessionId
      state.duration = 0
      state.transcriptSegments = []
      state.proposals = []
      state.error = null
      state.panelExpanded = true
    },

    setConnecting: (state) => {
      state.status = 'connecting'
    },

    pauseStreaming: (state) => {
      state.status = 'paused'
    },

    resumeStreaming: (state) => {
      state.status = 'streaming'
    },

    stopStreaming: (state) => {
      state.status = 'stopped'
    },

    // Duration update
    updateDuration: (state, action: PayloadAction<number>) => {
      state.duration = action.payload
    },

    // Transcript updates
    addTranscriptSegment: (state, action: PayloadAction<TranscriptSegment>) => {
      state.transcriptSegments.push(action.payload)
    },

    addTranscriptSegments: (state, action: PayloadAction<TranscriptSegment[]>) => {
      state.transcriptSegments.push(...action.payload)
    },

    // Update the last segment's speaker (used for speaker swap correction)
    updateLastSegmentSpeaker: (state, action: PayloadAction<{ newSpeaker: string }>) => {
      if (state.transcriptSegments.length > 0) {
        state.transcriptSegments[state.transcriptSegments.length - 1].speaker = action.payload.newSpeaker
      }
    },

    // The clinician sent the pending suggestion verbatim: convert the
    // suggestion entry into the spoken message in place — no duplicate line.
    markLastSuggestionSpoken: (state, action: PayloadAction<{ speaker: string }>) => {
      for (let i = state.transcriptSegments.length - 1; i >= 0; i--) {
        if (state.transcriptSegments[i].source === 'agent_suggestion') {
          state.transcriptSegments[i].source = 'spoken'
          state.transcriptSegments[i].speaker = action.payload.speaker
          break
        }
      }
    },

    // Flow context updates
    setFlowContext: (state, action: PayloadAction<FlowContext | null>) => {
      state.flowContext = action.payload
    },

    addDismissedFlow: (state, action: PayloadAction<{ flow_name: string; reason: string }>) => {
      state.dismissedFlows.push(action.payload)
      state.flowContext = null
    },

    // Flow orchestration
    setFlowTaskQueue: (state, action: PayloadAction<{ tasks: FlowTask[]; advanceMode?: string }>) => {
      state.flowTaskQueue = action.payload.tasks.filter(t => t.status === 'queued')
      state.currentFlowTask = action.payload.tasks.find(t => t.status === 'running') || null
      state.completedFlowTasks = action.payload.tasks.filter(t => t.status === 'completed')
      state.flowStatus = state.currentFlowTask ? 'running' : 'idle'
      if (action.payload.advanceMode) {
        state.advanceMode = action.payload.advanceMode as 'auto' | 'hybrid' | 'manual'
      }
    },

    setFlowCompleted: (state, action: PayloadAction<FlowCompletedEvent>) => {
      const { completed_task, next_task } = action.payload
      if (completed_task) {
        // Move from current to completed
        state.completedFlowTasks.push({ ...completed_task, status: 'completed' })
      }
      state.currentFlowTask = null
      state.flowStatus = next_task ? 'paused' : 'complete'
    },

    setTaskProgress: (state, action: PayloadAction<TaskProgressEvent>) => {
      const { current_task } = action.payload
      state.currentFlowTask = { ...current_task, status: 'running' }
      // Remove from queue if present
      state.flowTaskQueue = state.flowTaskQueue.filter(t => t.task_id !== current_task.task_id)
      state.flowStatus = 'running'
    },

    setFlowStopped: (state, action: PayloadAction<{ flow_name: string; adhoc?: boolean }>) => {
      if (action.payload.adhoc) {
        // Ad-hoc flow was stopped — just clear it (no queue to return to)
        state.currentAdhocFlow = null
      } else {
        // Planned flow stopped — return to queue
        if (state.currentFlowTask) {
          state.flowTaskQueue.unshift({ ...state.currentFlowTask, status: 'queued' })
        }
        state.currentFlowTask = null
        state.flowStatus = 'paused'
      }
      state.flowContext = null
    },

    setEncounterComplete: (state, action: PayloadAction<EncounterFlowsCompleteEvent>) => {
      state.flowStatus = 'complete'
      state.currentFlowTask = null
      state.flowTaskQueue = []
      // Rebuild completed list from event data
      state.completedFlowTasks = action.payload.completed_tasks.map(t => ({
        ...t,
        display_order: 0,
        status: 'completed' as const,
      }))
    },

    // Ad-hoc flow tracking
    setAdhocFlowStarted: (state, action: PayloadAction<AdhocFlowStartedEvent>) => {
      state.currentAdhocFlow = action.payload
    },

    setAdhocFlowCompleted: (state, action: PayloadAction<AdhocFlowCompletedEvent>) => {
      state.adhocFlowTasks.push(action.payload)
      state.currentAdhocFlow = null
    },

    // Proposal updates (dedup by id to prevent HTTP+SSE duplicates)
    addProposal: (state, action: PayloadAction<AgentProposal>) => {
      if (action.payload.id && state.proposals.some(p => p.id === action.payload.id)) {
        return  // Skip duplicate
      }
      state.proposals.push(action.payload)
    },

    addProposals: (state, action: PayloadAction<AgentProposal[]>) => {
      for (const proposal of action.payload) {
        if (proposal.id && state.proposals.some(p => p.id === proposal.id)) {
          continue  // Skip duplicate
        }
        state.proposals.push(proposal)
      }
    },

    // Update proposal decision
    updateProposalDecision: (
      state,
      action: PayloadAction<{ proposalId: string; decision: ProposalDecision }>
    ) => {
      const proposal = state.proposals.find((p) => p.id === action.payload.proposalId)
      if (proposal) {
        proposal.decision = action.payload.decision
        proposal.decision_timestamp = new Date().toISOString()
      }
    },

    // Update proposal execution status
    updateProposalExecution: (
      state,
      action: PayloadAction<{
        proposalId: string
        status: ExecutionStatus
        error?: string
      }>
    ) => {
      const proposal = state.proposals.find((p) => p.id === action.payload.proposalId)
      if (proposal) {
        proposal.execution_status = action.payload.status
        proposal.execution_error = action.payload.error
      }
    },

    // Update a slot fill value after provider correction
    updateSlotFill: (
      state,
      action: PayloadAction<{ slot_name: string; new_value: any; source: string }>
    ) => {
      // Update slot fill in all proposals that contain this slot
      for (const proposal of state.proposals) {
        if (proposal.slot_fills) {
          const fill = proposal.slot_fills.find((f) => f.slot_name === action.payload.slot_name)
          if (fill) {
            fill.value = action.payload.new_value
            fill.source = 'user_corrected'
          }
        }
      }
      // Also update in flow context
      if (state.flowContext?.slot_fills) {
        const fill = state.flowContext.slot_fills.find((f) => f.slot_name === action.payload.slot_name)
        if (fill) {
          fill.value = action.payload.new_value
          fill.source = 'user_corrected'
        }
      }
    },

    // Error handling
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.status = 'error'
    },

    clearError: (state) => {
      state.error = null
    },

    // UI state
    togglePanel: (state) => {
      state.panelExpanded = !state.panelExpanded
    },

    expandPanel: (state) => {
      state.panelExpanded = true
    },

    collapsePanel: (state) => {
      state.panelExpanded = false
    },

    toggleTranscript: (state) => {
      state.transcriptVisible = !state.transcriptVisible
    },

    toggleProposals: (state) => {
      state.proposalsVisible = !state.proposalsVisible
    },

    // Reset
    resetAgentEval: () => {
      return { ...initialState }
    },

    // Reset but keep UI preferences
    resetSession: (state) => {
      state.status = 'idle'
      state.sessionId = null
      state.duration = 0
      state.patientId = null
      state.patientName = null
      state.transcriptSegments = []
      state.proposals = []
      state.flowContext = null
      state.dismissedFlows = []
      state.flowTaskQueue = []
      state.currentFlowTask = null
      state.completedFlowTasks = []
      state.flowStatus = 'idle'
      state.currentAdhocFlow = null
      state.adhocFlowTasks = []
      state.error = null
      // Keep UI preferences: panelExpanded, transcriptVisible, proposalsVisible, advanceMode
    }
  }
})

export const {
  startStreaming,
  setConnecting,
  pauseStreaming,
  resumeStreaming,
  stopStreaming,
  updateDuration,
  addTranscriptSegment,
  addTranscriptSegments,
  updateLastSegmentSpeaker,
  markLastSuggestionSpoken,
  setFlowContext,
  addDismissedFlow,
  setFlowTaskQueue,
  setFlowCompleted,
  setFlowStopped,
  setTaskProgress,
  setEncounterComplete,
  addProposal,
  addProposals,
  updateProposalDecision,
  updateProposalExecution,
  updateSlotFill,
  setAdhocFlowStarted,
  setAdhocFlowCompleted,
  setError,
  clearError,
  togglePanel,
  expandPanel,
  collapsePanel,
  toggleTranscript,
  toggleProposals,
  resetAgentEval,
  resetSession
} = agentEvalSlice.actions

export default agentEvalSlice.reducer
